import React, { useState, useEffect } from 'react';
import { 
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Paper, CircularProgress, Snackbar, Alert, TextField,
  IconButton, FormControl, InputLabel, Select, MenuItem, Tab, Tabs
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { 
  getTeacherSubjects, createTeacherSubject, updateTeacherSubject, deleteTeacherSubject,
  getTeachers, getSubjects, getDepartments
} from '../services/api';

const DepartmentTeacherSubjects = ({ departmentId }) => {
  const [teacherSubjects, setTeacherSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currentTeacherSubject, setCurrentTeacherSubject] = useState({
    teacher_id: '',
    subject_id: '',
    department_id: departmentId || ''
  });
  const [selectedId, setSelectedId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [subjectFilter, setSubjectFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState(departmentId || '');
  const [activeTab, setActiveTab] = useState(0);
  
  const columns = [
    { field: 'teacher_subject_id', headerName: 'ID', width: 70 },
    { field: 'teacher_name', headerName: 'Teacher', flex: 1 },
    { field: 'subject_name', headerName: 'Subject', width: 200 },
    { field: 'subject_code', headerName: 'Subject Code', width: 150 },
    { field: 'department_name', headerName: 'Department', width: 180 },
    { field: 'is_lab', headerName: 'Lab', width: 80, renderCell: (params) => (
      params.row.is_lab ? 'Yes' : 'No'
    )},
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      width: 120,
      renderCell: (params) => (
        <Box>
          <IconButton 
            color="primary" 
            size="small"
            onClick={() => handleEdit(params.row)}
          >
            <EditIcon />
          </IconButton>
          <IconButton 
            color="error" 
            size="small"
            onClick={() => handleDeleteClick(params.row.teacher_subject_id)}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  useEffect(() => {
    fetchAllData();
  }, [departmentId]);

  // Fetch all necessary data
  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchTeacherSubjects(),
        fetchTeachers(),
        fetchSubjects(),
        fetchDepartments()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeacherSubjects = async () => {
    try {
      const response = await getTeacherSubjects();
      // If departmentId is provided, filter the teacher-subject mappings
      const mappings = response.data;
      setTeacherSubjects(mappings);
    } catch (error) {
      console.error('Error fetching teacher-subjects:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load teacher-subject mappings',
        severity: 'error'
      });
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await getTeachers();
      setTeachers(response.data);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await getSubjects();
      setSubjects(response.data);
      setFilteredSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await getDepartments();
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setCurrentTeacherSubject({
      teacher_id: '',
      subject_id: '',
      department_id: departmentId || ''
    });
    setSelectedId(null);
    setFormErrors({});
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleEdit = (teacherSubject) => {
    setCurrentTeacherSubject({
      teacher_id: teacherSubject.teacher_id,
      subject_id: teacherSubject.subject_id,
      department_id: teacherSubject.department_id || departmentId || ''
    });
    setSelectedId(teacherSubject.teacher_subject_id);
    setOpenDialog(true);
    setFormErrors({});
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteTeacherSubject(selectedId);
      setTeacherSubjects(teacherSubjects.filter(ts => ts.teacher_subject_id !== selectedId));
      setSnackbar({
        open: true,
        message: 'Teacher-Subject mapping deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting teacher-subject mapping:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete teacher-subject mapping',
        severity: 'error'
      });
    } finally {
      setConfirmDelete(false);
      setSelectedId(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentTeacherSubject({ ...currentTeacherSubject, [name]: value });
    
    // Clear the error for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }

    // If subject department changes, filter subjects
    if (name === 'department_id') {
      filterSubjectsByDepartment(value);
    }
  };

  const filterSubjectsByDepartment = (departmentId) => {
    if (!departmentId) {
      setFilteredSubjects(subjects);
    } else {
      setFilteredSubjects(subjects.filter(subject => 
        subject.department_id === parseInt(departmentId) || !subject.department_id
      ));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!currentTeacherSubject.teacher_id) {
      errors.teacher_id = 'Teacher is required';
    }
    if (!currentTeacherSubject.subject_id) {
      errors.subject_id = 'Subject is required';
    }
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      // Find teacher and subject details for display
      const teacher = teachers.find(t => t.teacher_id === parseInt(currentTeacherSubject.teacher_id));
      const subject = subjects.find(s => s.subject_id === parseInt(currentTeacherSubject.subject_id));
      const department = departments.find(d => d.department_id === parseInt(currentTeacherSubject.department_id || departmentId));
      
      if (selectedId) {
        // Update existing teacher-subject mapping
        await updateTeacherSubject(selectedId, currentTeacherSubject);
        
        setTeacherSubjects(teacherSubjects.map(ts => 
          ts.teacher_subject_id === selectedId 
            ? { 
                ...ts, 
                ...currentTeacherSubject, 
                teacher_subject_id: selectedId,
                teacher_name: teacher?.name,
                subject_name: subject?.subject_name,
                subject_code: subject?.subject_code,
                department_name: department?.department_name,
                is_lab: subject?.is_lab
              } 
            : ts
        ));
        
        setSnackbar({
          open: true,
          message: 'Teacher-Subject mapping updated successfully',
          severity: 'success'
        });
      } else {
        // Create new teacher-subject mapping
        const response = await createTeacherSubject(currentTeacherSubject);
        
        const newTeacherSubject = {
          ...currentTeacherSubject,
          teacher_subject_id: response.data.teacher_subject_id,
          teacher_name: teacher?.name,
          subject_name: subject?.subject_name,
          subject_code: subject?.subject_code,
          department_name: department?.department_name,
          is_lab: subject?.is_lab
        };
        
        setTeacherSubjects([...teacherSubjects, newTeacherSubject]);
        setSnackbar({
          open: true,
          message: 'Teacher-Subject mapping created successfully',
          severity: 'success'
        });
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving teacher-subject mapping:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save teacher-subject mapping',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenFilterDialog = () => {
    setFilterDialogOpen(true);
  };

  const handleCloseFilterDialog = () => {
    setFilterDialogOpen(false);
  };

  const applyFilter = () => {
    let filtered = teacherSubjects;
    
    if (subjectFilter) {
      const searchTerm = subjectFilter.toLowerCase();
      filtered = filtered.filter(ts => 
        ts.subject_name.toLowerCase().includes(searchTerm) || 
        ts.subject_code.toLowerCase().includes(searchTerm)
      );
    }
    
    if (departmentFilter) {
      filtered = filtered.filter(ts => ts.department_id === parseInt(departmentFilter));
    }
    
    setTeacherSubjects(filtered);
    handleCloseFilterDialog();
  };

  const resetFilter = async () => {
    setSubjectFilter('');
    setDepartmentFilter(departmentId || '');
    await fetchTeacherSubjects();
    handleCloseFilterDialog();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      // All mappings
      fetchTeacherSubjects();
    } else if (newValue === 1) {
      // Lab mappings
      const labMappings = teacherSubjects.filter(ts => ts.is_lab);
      setTeacherSubjects(labMappings);
    } else if (newValue === 2) {
      // Theory mappings
      const theoryMappings = teacherSubjects.filter(ts => !ts.is_lab);
      setTeacherSubjects(theoryMappings);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="All Mappings" />
          <Tab label="Lab Mappings" />
          <Tab label="Theory Mappings" />
        </Tabs>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<FilterListIcon />}
            onClick={handleOpenFilterDialog}
            sx={{ mr: 1 }}
          >
            Filter
          </Button>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Add Mapping
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={teacherSubjects}
            columns={columns}
            getRowId={(row) => row.teacher_subject_id}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 25]}
            checkboxSelection={false}
            disableSelectionOnClick
            autoHeight
          />
        )}
      </Paper>

      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onClose={handleCloseFilterDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Filter Teacher-Subject Mappings</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Subject Search"
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              placeholder="Search by subject name or code"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Department</InputLabel>
              <Select
                value={departmentFilter}
                label="Department"
                onChange={(e) => setDepartmentFilter(e.target.value)}
              >
                <MenuItem value="">All Departments</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.department_id} value={dept.department_id}>
                    {dept.department_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetFilter}>Reset</Button>
          <Button onClick={applyFilter} variant="contained" color="primary">
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Teacher-Subject Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedId ? 'Edit Teacher-Subject Mapping' : 'Add Teacher-Subject Mapping'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Department</InputLabel>
              <Select
                name="department_id"
                value={currentTeacherSubject.department_id}
                label="Department"
                onChange={handleInputChange}
              >
                <MenuItem value="">Select Department</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.department_id} value={dept.department_id}>
                    {dept.department_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth margin="normal" error={!!formErrors.teacher_id}>
              <InputLabel>Teacher</InputLabel>
              <Select
                name="teacher_id"
                value={currentTeacherSubject.teacher_id}
                label="Teacher"
                onChange={handleInputChange}
              >
                {teachers.map((teacher) => (
                  <MenuItem key={teacher.teacher_id} value={teacher.teacher_id}>
                    {teacher.name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.teacher_id && (
                <Box sx={{ color: 'error.main', mt: 0.5, fontSize: '0.75rem' }}>
                  {formErrors.teacher_id}
                </Box>
              )}
            </FormControl>
            
            <FormControl fullWidth margin="normal" error={!!formErrors.subject_id}>
              <InputLabel>Subject</InputLabel>
              <Select
                name="subject_id"
                value={currentTeacherSubject.subject_id}
                label="Subject"
                onChange={handleInputChange}
              >
                {filteredSubjects.map((subject) => (
                  <MenuItem key={subject.subject_id} value={subject.subject_id}>
                    {subject.subject_name} ({subject.subject_code}) {subject.is_lab ? '- Lab' : ''}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.subject_id && (
                <Box sx={{ color: 'error.main', mt: 0.5, fontSize: '0.75rem' }}>
                  {formErrors.subject_id}
                </Box>
              )}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this teacher-subject mapping? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DepartmentTeacherSubjects;
